import PeopleIcon from '@mui/icons-material/People';
import { ListItem, ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { TypographyWithThemeColor } from 'components/TypographyWithThemeColor';
import { BrandVoiceOption } from 'features/brand-voice/components/AutocompleteDropdownMenuContent';
import { BrandVoiceName } from 'features/brand-voice/components/BrandVoiceName';
import { BrandVoiceThumbnailAvatar } from 'features/brand-voice/components/BrandVoiceThumbnailAvatar';
import React from 'react';
import styled from 'styled-components';

type Props = {
  option: BrandVoiceOption;
  isPersonal?: boolean;
  searchText?: string;
};

type HighlightedTextProps = {
  text: string;
  searchText: string;
};

export const HighlightedText = ({ text, searchText }: HighlightedTextProps) => {
  if (!searchText || searchText.length === 0) {
    return <>{text}</>;
  }

  const parts = text.split(
    new RegExp(`(${searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'i')
  );

  return (
    <>
      {parts.map((part, index) => {
        const isMatch = part.toLowerCase() === searchText.toLowerCase();
        // Create a unique key using both the index and a prefix indicating if it's a match
        const key = `${isMatch ? 'match' : 'text'}-${index}-${part}`;
        return isMatch ? <b key={key}>{part}</b> : <span key={key}>{part}</span>;
      })}
    </>
  );
};

export const BrandVoiceMenuItemContent = ({
  option,
  searchText = '',
  isPersonal = true
}: Props) => (
  <>
    <ListItemIcon>
      <BrandVoiceThumbnailAvatar thumbnailUrl={option.thumbnailUrl} />
    </ListItemIcon>

    <ListItemText>
      <OptionText>
        <BrandVoiceName
          name={
            searchText ? (
              <HighlightedText
                text={option.name || 'Unnamed Brand Voice'}
                searchText={searchText}
              />
            ) : (
              option.name
            )
          }
        />

        {option?.brand?.name && (
          <BrandName variant="body2" color="textDisabled" noWrap={true}>
            {searchText ? (
              <HighlightedText text={option.brand.name} searchText={searchText} />
            ) : (
              option.brand.name
            )}
          </BrandName>
        )}
      </OptionText>
    </ListItemText>

    {!isPersonal && <PeopleIcon color="action" fontSize="small" />}
  </>
);

const OptionText = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  max-width: 280px;
  column-gap: ${({ theme }) => theme.spacings.two};

  align-items: center;
`;

const BrandName = styled(TypographyWithThemeColor)`
  margin-left: ${({ theme }) => theme.spacings.two};
  max-width: 150px;
`;
